import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Briefcase, MapPin, Wallet, Lock, Star } from "lucide-react";
import { StatusBadge } from "@/components/site/StatusBadge";
import { api, formatApiError, fmtDate } from "@/lib/api";
import { JOB_TYPES, payLabel, whereLabel } from "@/lib/community";

export function JobMeta({ j }) {
  return (
    <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-slate-600">
      <span className="inline-flex items-center gap-1"><Briefcase className="h-4 w-4 text-[#1A9AA8]" />{JOB_TYPES[j.job_type]}</span>
      <span className="inline-flex items-center gap-1"><MapPin className="h-4 w-4 text-[#1A9AA8]" />{whereLabel(j)}</span>
      <span className="inline-flex items-center gap-1 font-semibold text-[#0B2A4A]"><Wallet className="h-4 w-4 text-[#F08A3C]" />{payLabel(j)}</span>
    </div>
  );
}

export function JobCard({ j, featured = false }) {
  return (
    <Link to={`/jobs/${j.id}`} className={`block rounded-2xl border p-6 hover:-translate-y-0.5 transition-transform ${featured ? "bg-[#FFF7EE] border-[#F08A3C] ring-1 ring-[#F08A3C]/40" : "bg-white border-[#0B2A4A]/10 hover:border-[#1A9AA8]"}`} data-testid={`${featured ? "featured-" : ""}job-card-${j.id}`}>
      {featured && <p className="mb-2 inline-flex items-center gap-1 rounded-full bg-[#F08A3C] text-white px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wider"><Star className="h-3 w-3" /> Top job</p>}
      <p className="text-xs font-mono-ui text-slate-500">{j.company} · {fmtDate(j.approved_at)}</p>
      <h3 className="mt-1 text-lg font-semibold text-[#0B2A4A]">{j.title}</h3>
      <div className="mt-2"><JobMeta j={j} /></div>
      {j.full ? <p className="mt-3 text-sm text-slate-600 line-clamp-2">{j.description}</p> : <p className="mt-3 text-sm text-slate-600">{j.teaser} <span className="inline-flex items-center gap-1 text-[#D96F28] font-semibold"><Lock className="h-3.5 w-3.5" /> Members see the full job</span></p>}
    </Link>
  );
}

function ApplicantRow({ a, onStatus }) {
  return (
    <div className="rounded-xl border border-[#0B2A4A]/10 p-4" data-testid={`applicant-${a.id}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="font-semibold text-[#0B2A4A]">{a.applicant_name} <span className="font-normal text-sm text-slate-500">· {a.applicant_email}</span></p>
        <select value={a.status} onChange={(e) => onStatus(a.id, e.target.value)} className="h-9 rounded-md border px-2 text-sm" data-testid={`applicant-status-${a.id}`}>
          {["new", "shortlisted", "contacted", "declined"].map((s) => <option key={s} value={s}>{s[0].toUpperCase() + s.slice(1)}</option>)}
        </select>
      </div>
      <p className="mt-2 text-sm text-slate-700 whitespace-pre-wrap">{a.message}</p>
      <p className="mt-2 text-xs text-slate-500">{[a.profile?.what_i_do, a.profile?.works_from, a.portfolio_url, a.whatsapp && `WhatsApp ${a.whatsapp}`].filter(Boolean).join(" · ")}</p>
    </div>
  );
}

function FeatureBox({ j, reload }) {
  const [weeks, setWeeks] = useState(1);
  const live = j.status === "approved" && j.featured_until && new Date(j.featured_until) > new Date();
  const req = j.feature_request;
  const send = () => api.post(`/jobs/${j.id}/feature-request`, { weeks }).then(() => { toast.success("Request sent. Greg will contact you about payment."); reload(); }).catch((e) => toast.error(formatApiError(e)));
  if (live) return <p className="mt-3 text-sm font-semibold text-[#D96F28]" data-testid={`my-post-featured-${j.id}`}><Star className="inline h-4 w-4" /> Featured in the top 3 until {fmtDate(j.featured_until)}</p>;
  if (!['approved', 'pending'].includes(j.status)) return null;
  if (req?.status === "requested") return <p className="mt-3 text-sm text-slate-600" data-testid={`my-post-feature-pending-${j.id}`}>Feature request sent: {req.weeks} week(s), ${req.amount}. Greg will contact you to arrange payment.</p>;
  return (
    <div className="mt-3 flex flex-wrap items-center gap-2 rounded-xl bg-[#FFF7EE] border border-[#F08A3C]/30 p-3" data-testid={`my-post-feature-box-${j.id}`}>
      <Star className="h-4 w-4 text-[#F08A3C]" /><span className="text-sm text-slate-700">Pin this job in the Top 3 for $50/week:</span>
      <select value={weeks} onChange={(e) => setWeeks(Number(e.target.value))} className="h-9 rounded-md border px-2 text-sm" data-testid={`feature-weeks-${j.id}`}>{[1, 2, 3, 4, 8, 12].map((w) => <option key={w} value={w}>{w} week{w > 1 ? "s" : ""} · ${w * 50}</option>)}</select>
      <button onClick={send} className="rounded-lg bg-[#F08A3C] text-white px-3 py-1.5 text-xs font-semibold" data-testid={`feature-request-btn-${j.id}`}>Feature my job</button>
    </div>
  );
}

export function MyPosts({ reloadKey }) {
  const [rows, setRows] = useState(null);
  const load = () => api.get("/jobs/mine").then((r) => setRows(r.data)).catch(() => setRows([]));
  useEffect(() => { load(); }, [reloadKey]);
  const onStatus = (id, status) => api.patch(`/jobs/applications/${id}`, { status }).then(load).catch((e) => toast.error(formatApiError(e)));
  const close = (id) => api.patch(`/jobs/${id}/close`).then(() => { toast.success("Job closed"); load(); });
  if (!rows) return null;
  if (!rows.length) return <p className="text-slate-600" data-testid="my-posts-empty">You haven't posted a job yet. Use "Post a job" to create one.</p>;
  return (
    <div className="flex flex-col gap-4" data-testid="my-posts-list">
      {rows.map((j) => (
        <div key={j.id} className="bg-white rounded-2xl border border-[#0B2A4A]/10 p-6" data-testid={`my-post-${j.id}`}>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div><h3 className="text-lg font-semibold text-[#0B2A4A]">{j.title}</h3><JobMeta j={j} /></div>
            <div className="flex items-center gap-2"><StatusBadge status={j.status} testId={`my-post-status-${j.id}`} />{j.status === "approved" && <button onClick={() => close(j.id)} className="text-xs font-semibold text-rose-700" data-testid={`my-post-close-${j.id}`}>Close job</button>}</div>
          </div>
          <FeatureBox j={j} reload={load} />
          <p className="mt-4 text-xs font-semibold uppercase tracking-wider text-[#147C88]">Applications ({j.applications.length})</p>
          <div className="mt-2 flex flex-col gap-3">{j.applications.length ? j.applications.map((a) => <ApplicantRow key={a.id} a={a} onStatus={onStatus} />) : <p className="text-sm text-slate-500">No applications yet.</p>}</div>
        </div>
      ))}
    </div>
  );
}

export function MyApplied() {
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get("/jobs/applied").then((r) => setRows(r.data)).catch(() => setRows([])); }, []);
  if (!rows) return null;
  if (!rows.length) return <p className="text-slate-600" data-testid="my-applied-empty">You haven't applied to any jobs yet.</p>;
  return (
    <div className="flex flex-col gap-3" data-testid="my-applied-list">
      {rows.map((a) => (
        <Link key={a.id} to={`/jobs/${a.job_id}`} className="bg-white rounded-xl border border-[#0B2A4A]/10 p-4 flex flex-wrap justify-between gap-2" data-testid={`my-applied-${a.id}`}>
          <span className="font-semibold text-[#0B2A4A]">{a.job?.title} <span className="font-normal text-slate-500">· {a.job?.company}</span></span>
          <span className="text-sm text-slate-500">Applied {fmtDate(a.created_at)} · {a.status}</span>
        </Link>
      ))}
    </div>
  );
}
